import { ListChecks } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/shadcn/tabs";
import { searchContactUdLookup } from "@/api/autocomplete/api";
import { validateUdNumber, toSelectOptions } from "@/utils/fieldValidation";
import type { ContactUserDefinedGroup } from "@/types/domain";
import { InlineEditField } from "@/components/common/InlineEditField";
import { SectionHeading } from "./SectionHeading";

type Props = {
  groups: ContactUserDefinedGroup[];
  patchUd: (groupIdx: number, itemIdx: number, itemId: number) => (value: string) => Promise<void>;
  activeEditKey: string | null;
  onActivateEdit: (key: string | null) => void;
};

export const ContactUdFields = ({ groups, patchUd, activeEditKey, onActivateEdit }: Props) => {
  const ep = (key: string) => ({ editKey: key, activeEditKey, onActivate: onActivateEdit });

  return (
    <div className="px-4">
      <SectionHeading icon={<ListChecks size={15} />}>User Defined Fields</SectionHeading>
      {groups.length > 0 ? (
        <Tabs defaultValue={groups[0].name} className="mt-2">
          <TabsList className="flex flex-wrap h-auto gap-1 justify-start bg-transparent p-0 mb-2">
            {groups.map((group) => (
              <TabsTrigger
                key={group.name}
                value={group.name}
                className="border border-gray-200 data-[state=active]:border-gray-400 data-[state=active]:bg-white"
              >
                {group.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {groups.map((group, gi) => (
            <TabsContent key={group.name} value={group.name}>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-7">
                {group.items.map((item, ii) => (
                  <InlineEditField
                    {...ep(`ud-${item.itemId}`)}
                    key={ii}
                    label={item.name}
                    value={item.value}
                    onSave={patchUd(gi, ii, item.itemId)}
                    options={item.isLookup && item.lookupValues ? item.lookupValues : undefined}
                    onSearchOptions={item.isAutoComplete ? (q) => searchContactUdLookup(item.itemId, q).then(toSelectOptions) : undefined}
                    isBoolean={item.format === "Boolean"}
                    inputType={
                      item.format === "Date" ? "date" :
                      item.format === "Integer" ? "number" :
                      "text"
                    }
                    isMandatory={item.isMandatory}
                    isReadOnly={item.isReadOnly}
                    validate={(item.format === "Integer" || item.format === "Float")
                      ? (v) => validateUdNumber(item.format, item.decimalPlaces, item.name, v)
                      : undefined
                    }
                  />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <p className="text-sm text-muted-foreground py-2">No user defined fields.</p>
      )}
    </div>
  );
};
